/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid, Tooltip } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    // padding: theme.spacing(1),
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

// Função utilitária para ordenar campanhas por status e agendamento
const statusOrder = {
  EM_ANDAMENTO: 0, // Em Andamento
  PROGRAMADA: 1,   // Programada
  FINALIZADA: 2,   // Finalizada
  // Outros status vêm depois
};

function sortCampaigns(a, b) {
  const statusA = statusOrder[a.status] !== undefined ? statusOrder[a.status] : 99;
  const statusB = statusOrder[b.status] !== undefined ? statusOrder[b.status] : 99;
  if (statusA !== statusB) return statusA - statusB;
  // Ordena por data de agendamento (scheduledAt) crescente
  const dateA = a.scheduledAt ? new Date(a.scheduledAt) : new Date(0);
  const dateB = b.scheduledAt ? new Date(b.scheduledAt) : new Date(0);
  return dateA - dateB;
}

// Função para formatar o status
const formatStatus = (val) => {
  switch (val) {
    case "INATIVA":
      return "Inativa";
    case "PROGRAMADA":
      return "Programada";
    case "EM_ANDAMENTO":
      return "Em Andamento";
    case "CANCELADA":
      return "Cancelada";
    case "FINALIZADA":
      return "Finalizada";
    default:
      return val;
  }
};

// Componente Badge minimalista para status
const StatusBadge = ({ status }) => {
  let color = "#bdbdbd";
  let label = formatStatus(status);
  switch (status) {
    case "PROGRAMADA":
      color = "#1976d2"; // azul
      break;
    case "EM_ANDAMENTO":
      color = "#ffb300"; // amarelo
      break;
    case "FINALIZADA":
      color = "#43a047"; // verde
      break;
    default:
      color = "#bdbdbd"; // cinza
  }
  return (
    <span style={{
      display: "inline-block",
      minWidth: 70,
      padding: "2px 10px",
      borderRadius: 12,
      background: color,
      color: "#fff",
      fontSize: 12,
      fontWeight: 500,
      textAlign: "center"
    }}>{label}</span>
  );
};

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const [duplicateTargetCampaign, setDuplicateTargetCampaign] = useState(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateMode, setDuplicateMode] = useState("all");
  const [duplicateWhatsappId, setDuplicateWhatsappId] = useState("");
  const [duplicateScheduledAt, setDuplicateScheduledAt] = useState("");
  const [whatsapps, setWhatsapps] = useState([]);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyCampaign = (data) => {
      // Se for delete, tratamos diretamente com o reducer
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
        return;
      }

      // Para create ou update, recarregamos a lista de campanhas do backend
      // para garantir que apenas campanhas apropriadas sejam exibidas
      if (data.action === "update" || data.action === "create") {
        console.log(`Campanha ${data.action === "create" ? "criada" : "atualizada"}, recarregando dados...`);
        fetchCampaigns();
      }
    };

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const params = { searchParam, pageNumber };
      
      // Se o usuário não for admin, adicionar userId como filtro
      if (user.profile !== "admin") {
        params.userId = user.id;
      }
      
      const { data } = await api.get("/campaigns/", {
        params
      });
      
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDateTimeLocal = (dateObj) => {
    const pad = (n) => String(n).padStart(2, "0");
    const y = dateObj.getFullYear();
    const m = pad(dateObj.getMonth() + 1);
    const d = pad(dateObj.getDate());
    const hh = pad(dateObj.getHours());
    const mm = pad(dateObj.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
  };

  const openDuplicateModal = (campaign) => {
    setDuplicateTargetCampaign(campaign);
    setDuplicateName(`${campaign.name} | Reenvio`);
    setDuplicateMode("all");
    setDuplicateWhatsappId(campaign.whatsappId || "");
    setDuplicateScheduledAt(formatDateTimeLocal(new Date(Date.now() + 5 * 60 * 1000)));
    setDuplicateModalOpen(true);
  };

  const closeDuplicateModal = () => {
    setDuplicateModalOpen(false);
    setDuplicateTargetCampaign(null);
  };

  const handleDuplicateCampaign = async () => {
    if (!duplicateTargetCampaign) return;
    try {
      const scheduledIso =
        duplicateScheduledAt
          ? new Date(duplicateScheduledAt).toISOString()
          : undefined;
      const payload = {
        mode: duplicateMode,
        whatsappId: duplicateWhatsappId || undefined,
        scheduledAt: scheduledIso,
        name: duplicateName
      };
      await api.post(`/campaigns/${duplicateTargetCampaign.id}/duplicate`, payload);
      toast.success(
        `Campanha duplicada (${duplicateMode === "all" ? "todos" : "somente não entregue"})`
      );
      closeDuplicateModal();
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    const loadWhatsapps = async () => {
      try {
        const { data } = await api.get(`/whatsapp`, {
          params: { companyId: user.companyId, session: 0 }
        });
        const list = user.profile === "admin" ? data : data.filter(w => w.id === user.whatsappId);
        setWhatsapps(list || []);
      } catch (err) {
        // silencioso
      }
    };
    if (duplicateModalOpen) {
      loadWhatsapps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duplicateModalOpen]);

  // Verificar se o usuário pode manipular a campanha
  const canManageCampaign = (campaign) => {
    // Se o usuário for admin, tem permissão
    if (user.profile === "admin") return true;
    
    // Verificar se o ID do usuário está no formato correto
    const campaignUserId = campaign.userId || (campaign.user && campaign.user.id);
    
    // Verifica se o usuário atual é o criador da campanha
    return campaignUserId === user.id;
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {
        user.profile === "user" && user.allowCampaigns === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <MainHeader>
              <Grid style={{ width: "99.6%" }} container>
                <Grid xs={12} sm={8} item>
                  <Title>{i18n.t("campaigns.title")}</Title>
                </Grid>
                <Grid xs={12} sm={4} item>
                  <Grid spacing={2} container>
                    <Grid xs={6} sm={6} item>
                      <TextField
                        fullWidth
                        placeholder={i18n.t("campaigns.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon style={{ color: "gray" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid xs={6} sm={6} item>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleOpenCampaignModal}
                        color="primary"
                      >
                        {i18n.t("campaigns.buttons.add")}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </MainHeader>
            <Paper
              className={classes.mainPaper}
              variant="outlined"
              onScroll={handleScroll}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.name")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.owner")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.status")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.contactList")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.whatsapp")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.scheduledAt")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.completedAt")}
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.confirmation")}
                    </TableCell>
                    <TableCell align="center">
                      Configurações
                    </TableCell>
                    <TableCell align="center">
                      {i18n.t("campaigns.table.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {campaigns
                      .slice() // evitar mutação
                      .sort(sortCampaigns)
                      .map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell align="center">{campaign.name}</TableCell>
                          <TableCell align="center">
                            {campaign.user ? campaign.user.name : "Não definido"}
                          </TableCell>
                          <TableCell align="center">
                            <StatusBadge status={campaign.status} />
                          </TableCell>
                          <TableCell align="center">
                            {campaign.contactListId
                              ? campaign.contactList.name
                              : "Não definida"}
                          </TableCell>
                          <TableCell align="center">
                            {campaign.whatsappId
                              ? campaign.whatsapp.name
                              : "Não definido"}
                          </TableCell>
                          <TableCell align="center">
                            {campaign.scheduledAt
                              ? datetimeToClient(campaign.scheduledAt)
                              : "Sem agendamento"}
                          </TableCell>
                          <TableCell align="center">
                            {campaign.completedAt
                              ? datetimeToClient(campaign.completedAt)
                              : "Não concluída"}
                          </TableCell>
                          <TableCell align="center">
                            {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={campaign.useGlobalSettings ? "Usando configurações globais" : "Usando configurações específicas"}>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: campaign.useGlobalSettings ? '#e3f2fd' : '#f3e5f5',
                                border: `1px solid ${campaign.useGlobalSettings ? '#1976d2' : '#9c27b0'}`,
                                borderRadius: '12px',
                                padding: '0px 8px',
                                height: '24px'
                              }}>
                                <span style={{ 
                                  color: campaign.useGlobalSettings ? '#1976d2' : '#9c27b0',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}>
                                  {campaign.useGlobalSettings ? "Global" : "Específica"}
                                </span>
                              </div>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {campaign.status === "EM_ANDAMENTO" && canManageCampaign(campaign) && (
                              <IconButton
                                onClick={() => cancelCampaign(campaign)}
                                title="Parar Campanha"
                                size="small"
                              >
                                <PauseCircleOutlineIcon />
                              </IconButton>
                            )}
                            {campaign.status === "CANCELADA" && canManageCampaign(campaign) && (
                              <IconButton
                                onClick={() => restartCampaign(campaign)}
                                title="Reiniciar Campanha"
                                size="small"
                              >
                                <PlayCircleOutlineIcon />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={() =>
                                history.push(`/campaign/${campaign.id}/report`)
                              }
                              size="small"
                            >
                              <DescriptionIcon />
                            </IconButton>
                            {canManageCampaign(campaign) && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={() => openDuplicateModal(campaign)}
                                  title="Duplicar campanha"
                                >
                                  <FileCopyIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCampaign(campaign)}
                                >
                                  <EditIcon />
                                </IconButton>

                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    setConfirmModalOpen(true);
                                    setDeletingCampaign(campaign);
                                  }}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    {loading && <TableRowSkeleton columns={9} />}
                  </>
                </TableBody>
              </Table>
            </Paper>
            <Dialog open={duplicateModalOpen} onClose={closeDuplicateModal} maxWidth="sm" fullWidth>
              <DialogTitle>Duplicar Campanha</DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Nome"
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      value={duplicateName}
                      onChange={(e) => setDuplicateName(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="duplicate-whatsapp-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Select
                        labelId="duplicate-whatsapp-label"
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        value={duplicateWhatsappId}
                        onChange={(e) => setDuplicateWhatsappId(e.target.value)}
                      >
                        {whatsapps.map((w) => (
                          <MenuItem key={w.id} value={w.id}>
                            {w.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      type="datetime-local"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={duplicateScheduledAt}
                      onChange={(e) => setDuplicateScheduledAt(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel id="duplicate-mode-label">Modo de disparo</InputLabel>
                      <Select
                        labelId="duplicate-mode-label"
                        label="Modo de disparo"
                        value={duplicateMode}
                        onChange={(e) => setDuplicateMode(e.target.value)}
                      >
                        <MenuItem value="all">Enviar para todos</MenuItem>
                        <MenuItem value="undelivered">Somente não entregues</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {duplicateTargetCampaign && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Campanha original: {duplicateTargetCampaign.name}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDuplicateModal} color="secondary" variant="outlined">
                  {i18n.t("campaigns.settings.close")}
                </Button>
                <Button onClick={handleDuplicateCampaign} color="primary" variant="contained">
                  Duplicar
                </Button>
              </DialogActions>
            </Dialog>
          </>}
    </MainContainer>
  );
};

export default Campaigns;
